import { connect } from 'react-redux';
import React, { useEffect, useCallback, useState } from 'react';
import { isEmpty, isUndefined } from 'lodash';
import { toast } from 'react-toastify';

import { Box, Grid, TextField, Typography, CardActions } from '@material-ui/core'; // replaces <Segment>
import { Autocomplete } from '@material-ui/lab'; // replaces semantic-ui Dropdown
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';

import './_Scopebar.scss';

import { ModalBodyComponentProps } from 'src/components/Mfp/MfpScopebarOption/ScopebarOptionModal';
import { useHandleKeyPress } from 'src/utils/Component/hooks/hooks';
import { AppState, AppThunkDispatch } from 'src/store';
import { forceRefreshGrid, balanceScope } from 'src/state/scope/Scope.actions';
import { serverScopeMemberFromId } from './MfpScopeUtils';
import { getScopeReadyData } from 'src/state/scope/Scope.types';
import { ReseedPlanModalOwnProps } from 'src/components/Mfp/Reseed/ReseedPlanModal';

import AnchorRadioSelect from 'src/components/AnchorRadioSelect/AnchorRadioSelect';
import { Command } from 'src/state/scope/codecs/Commands';
import { keyToCommand } from 'src/state/scope/codecs/projections/PlanMetadataToDropdown';
import { anchorsFromCommandDetails, getBalanceCommands, hasCommands } from 'src/state/scope/codecs/Commands.utils';
import {
  useCommandToDropdown,
  useCommandToTimeDropdown,
  useCurrentCommands,
  useFocusButtonRef,
  useHandleChangeCommand,
  useSelectedCommand,
  useSelectedPlanId,
  useSelectedTime,
  useSetPlanId,
} from 'src/utils/Component/hooks/PlanManagement.hooks';
import { KeyboardArrowDown } from '@material-ui/icons';

const mapStateToProps = (state: AppState) => {
  const { settings } = state;
  const dimensionLabel = settings.dimensionLabelProperty;
  const readyScope = getScopeReadyData(state.mfpScope);

  if (!readyScope || isEmpty(readyScope.commands)) {
    return;
  }

  // map it twice, as hasCommands doesn't return the correct type
  // filter so that we don't get empty sets of commands
  const commands = readyScope.commands.map(getBalanceCommands).filter(hasCommands).map(getBalanceCommands);

  return {
    commands,
    dimensionLabel,
    // the below extracts the ServerScopeMembers from the set of plan
    anchor: serverScopeMemberFromId(anchorsFromCommandDetails(commands), readyScope.mainConfig.members),
    isMultiScope: readyScope.isMultiScope,
    entries: settings.entriesByKey,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    dispatchedBalanceScope: (balanceCommand: Command['command']) => {
      return dispatch(balanceScope(balanceCommand)).then(() => dispatch(forceRefreshGrid()));
    },
  };
};

export type EopToBopModalValueProps = ReturnType<typeof mapStateToProps>;
export type EopToBopModalDispatchProps = ReturnType<typeof mapDispatchToProps>;

type EopToBopModalProps = ModalBodyComponentProps &
  EopToBopModalValueProps &
  EopToBopModalDispatchProps &
  ReseedPlanModalOwnProps;

const EopToBopModal = (props: EopToBopModalProps) => {
  const { commands, onCancel, onSubmit, dispatchedBalanceScope, dimensionLabel, isMultiScope, anchor, entries } = props;

  const buttonRef = useFocusButtonRef();
  const [loading, setLoading] = useState(false);
  const [mutationPending, setMutationPending] = useState(false);

  const [selectedPlanId, setSelectedPlanId] = useSelectedPlanId(commands);
  const currentCommands = useCurrentCommands(commands, selectedPlanId);
  const [selectedTime, setSelectedTime] = useSelectedTime(currentCommands);
  const handleChangePlanId = useSetPlanId(commands, setSelectedPlanId);

  const versionOptions = useCommandToDropdown(commands, selectedPlanId, selectedTime, entries);
  const timeOptions = useCommandToTimeDropdown(currentCommands, entries);
  const [selectedCommand, setSelectedCommand] = useSelectedCommand(currentCommands);
  const handleChangeCommand = useHandleChangeCommand(setSelectedCommand);

  useEffect(() => {
    if (isEmpty(commands) && isUndefined(anchor)) {
      setLoading(false); // no data, doesn't need to remain loading
    } else if (isEmpty(commands)) {
      setLoading(true);
    }
  }, [commands, anchor]);

  const handleSubmit = useCallback(async () => {
    if (currentCommands && selectedCommand) {
      setMutationPending(true);
      const maybeFoundCommand = keyToCommand(selectedCommand, currentCommands.commands);
      if (!maybeFoundCommand) {
        setMutationPending(false);
        return;
      }
      await dispatchedBalanceScope(maybeFoundCommand.command)
        .catch(() => {
          toast.error('An error occurred copying EOP to BOP');
          throw new Error('An error occurred copying EOP to BOP');
        })
        .then(() => {
          if (!isMultiScope) {
            onSubmit(); // only close modal on success
          }
        })
        .finally(() => setMutationPending(false));
    }
  }, [currentCommands, selectedCommand, dispatchedBalanceScope, isMultiScope, onSubmit]);

  const handleEnterPress = useHandleKeyPress(handleSubmit);

  return (
    <>
      <div className="initialize-plan">
        <Grid container spacing={2}>
          <Grid className="column" item xs={12}>
            <Box mb={2} mt={2} p={2}>
              Select the Plan period and version to copy End of Plan data into the beginning of your WP
            </Box>

            {commands && dimensionLabel && anchor ? (
              <AnchorRadioSelect
                labelDimenion={dimensionLabel}
                anchor={anchor}
                onUpdateAnchorSelections={handleChangePlanId}
              />
            ) : null}

            <Box mt={2}>
              <Autocomplete
                loading={loading}
                loadingText="Loading..."
                className="no-border-style-input"
                popupIcon={<KeyboardArrowDown className="teal-arrow" />}
                fullWidth
                disableClearable
                data-qa={'eop-period-dropdown'}
                options={timeOptions}
                getOptionLabel={(option) => option.text ?? ''}
                value={timeOptions.find((o) => o.value === selectedTime) || undefined}
                onChange={(_evt, newValue) => {
                  setSelectedTime(newValue?.value ?? '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
              />
            </Box>

            <Box mt={2}>
              <Typography style={{ padding: '16px' }}>Select the Plan Version</Typography>
              <Autocomplete
                loading={loading}
                loadingText="Loading..."
                className="no-border-style-input"
                popupIcon={<KeyboardArrowDown className="teal-arrow" />}
                fullWidth
                disableClearable
                data-qa={'eop-version-dropdown'}
                options={versionOptions}
                getOptionLabel={(option) => option.text ?? ''}
                value={versionOptions.find((o) => o.value === selectedCommand) || undefined}
                onChange={handleChangeCommand}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                    }}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>
      </div>

      <CardActions>
        <SquareActionButton className="button" type="secondary" onClick={onCancel} text="Close" />

        <div
          ref={buttonRef}
          className="submit-button-modal"
          tabIndex={0}
          onKeyPress={handleEnterPress}
          style={{ display: 'inline-block', marginLeft: '0.5rem' }}
        >
          <SquareActionButton
            className="import-version-modal-button button"
            onClick={handleSubmit}
            onKeyPress={handleEnterPress}
            disabled={isUndefined(anchor) || loading || mutationPending}
            loading={mutationPending}
            text={mutationPending ? 'Submitting...' : 'Submit'}
          />
        </div>
      </CardActions>
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(EopToBopModal);
